import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { getNotificationsApi } from '../apis/notifications.api';
import { refreshToken } from '../utils/tokenManagement';

/**
 * Global hook to poll for new notifications and show toasts site-wide
 * Should be called once at app root level (e.g., in App.js)
 */
export const useNotificationPoller = () => {
  const seenIdsRef = useRef(new Set());
  const pollRef = useRef(null);
  const initialLoadRef = useRef(true);

  const pollNotifications = async () => {
    try {
      // Skip if not authenticated (avoids 401 redirects / reloads)
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await getNotificationsApi();
      const data = res?.data?.notifications ?? res?.data ?? [];
      const list = Array.isArray(data) ? data : [];

      // On initial load, just mark all as seen without showing toasts
      if (initialLoadRef.current) {
        const allIds = new Set(list.map((n) => n.id));
        seenIdsRef.current = allIds;
        initialLoadRef.current = false;
        // console.log('Initial load: marked', allIds.size, 'notifications as seen');
        return;
      }

      // Detect new unread notifications that arrived AFTER initial load
      const newOnes = list.filter((n) => !seenIdsRef.current.has(n.id));

      // console.log('Polling notifications:', {
      //   total: list.length,
      //   newOnes: newOnes.length,
      //   seenCount: seenIdsRef.current.size
      // });

      if (newOnes.length > 0) {
        // Show toast for each new notification (limit to 3, newest first)
        newOnes.slice(0, 3).forEach((n) => {
          const msg = n.body?.length > 120 ? `${n.body.slice(0, 120)}…` : n.body;
          // console.log('Showing toast for notification:', n.id, msg);
          
          // Use success type with more visible settings
          toast.success(msg || 'You have a new notification', {
            autoClose: 3000,
            toastId: `global-notif-${n.id}`,
            hideProgressBar: false,
            position: 'top-right',
            closeButton: true,
          });
        });

        // Update seen ids with new ones
        newOnes.forEach((n) => seenIdsRef.current.add(n.id));
      }
    } catch (err) {
      // Handle token expiration (403 with Firebase token error)
      if (err?.response?.status === 403 && err?.response?.data?.detail?.includes('Firebase token')) {
        console.log('[NOTIF POLLER] Firebase token expired, attempting refresh...');
        try {
          const newToken = await refreshToken();
          if (newToken) {
            console.log('[NOTIF POLLER] Token refreshed successfully');
            // Retry the notification fetch
            return pollNotifications();
          }
        } catch (refreshErr) {
          console.error('[NOTIF POLLER] Token refresh failed:', refreshErr);
        }
        return; // Stop polling if token refresh fails
      }
      
      // Swallow aborted / timeout errors to avoid noisy console & re-renders
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('aborted')) return;
      if (err?.name === 'CanceledError') return;

      // console.error('Failed to poll notifications:', err);
    }
  };

  useEffect(() => {
    // Poll every 12 seconds for new notifications (site-wide)
    pollRef.current = setInterval(pollNotifications, 12000);

    // Initial poll
    pollNotifications();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);
};
